// ─── Memorial Hall ───────────────────────────────────────────────────────────
//
// Two surfaces, both behind the same sign-in: the tile grid at `/`, and the
// calendar at `/calendar`. There is no public page — Crafting Comb has one
// because a customer has to be able to book without an account, and a hall
// committee has no equivalent.
//
// Almost all of this is `@nulife/*`: the shell, the sign-in form, the loading
// states, the API transport and the entire calendar screen. What is genuinely
// this product is the tile list next door, the routing below, and the four
// files in `src/` that wire the modules together. That is the platform's
// five-way separation working rather than being described.

import { Suspense, lazy, useEffect, useState, type ComponentType, type LazyExoticComponent } from "react";

// ── Chunk-loading resilience (2026-09-28) ───────────────────────────────────
//
// Real incident: a deploy renames every JS chunk (content-hashed
// filenames). A tab already open from before that deploy has the OLD
// chunk URLs baked into its already-loaded JS -- clicking into a module
// it has not visited yet in this page load tries to fetch a file that no
// longer exists on the server, and Vite's dynamic import throws a raw
// Failed to fetch dynamically imported module error, which the error
// boundary renders as a bare black error screen. Confirmed directly from
// the server's own client-error log, not guessed.
//
// Fix: catch that specific failure and reload once, automatically. A
// fresh index.html always carries the CURRENT chunk URLs, so the reload
// silently resolves it -- Drea never has to notice and refresh herself,
// which is exactly the failure mode she hit (repeatedly, ahead of a real
// presentation). Guarded by sessionStorage so a genuinely missing/broken
// file (not a stale-chunk case) fails after one retry instead of
// reload-looping forever.
function lazyWithReload<T extends { default: ComponentType<any> }>(
  importer: () => Promise<T>,
): LazyExoticComponent<T["default"]> {
  return lazy(async () => {
    try {
      const mod = await importer();
      sessionStorage.removeItem("nl-chunk-reloaded");
      return mod;
    } catch (err) {
      if (sessionStorage.getItem("nl-chunk-reloaded") === "1") {
        throw err;
      }
      sessionStorage.setItem("nl-chunk-reloaded", "1");
      window.location.reload();
      return new Promise<T>(() => {});
    }
  });
}

import "@nulife/booking-ui/theme.css";
import "@nulife/calendar-ui/theme.css";
import "@nulife/home-ui/theme.css";
// ── The chassis, 2026-09-24 ────────────────────────────────────────────────
//
// One import, and it is the whole of the shell's setup: full Tailwind
// INCLUDING Preflight, the Inter font, the base layer, the scrollbar treatment,
// Farndale's tokens and the tile artwork. There is no configuration step left
// for this app to get wrong, which is the point — the white gutters and the
// second scrollbar Drea photographed came from `@nulife/design-language`
// deliberately omitting Preflight and this app then not supplying it.
//
// It stays LAST of the theme imports on purpose: its tokens live under
// `data-nl-design`, a different attribute from the `data-nl-theme` the four
// module themes above use, so the two cannot collide whatever the order — but
// reading it last matches the cascade a reader would assume.
import "@nulife/chassis/styles.css";
import "@nulife/documents-ui/theme.css";
import "@nulife/compliance-ui/theme.css";
import { SignInForm, useAsync, Loading, Message } from "@nulife/app-ui";
// Not lazy: it is the first thing somebody sees when they follow a link, and
// a chunk fetch on a village hall phone signal is a blank screen at exactly
// the wrong moment.
import { AcceptInvite } from "./AcceptInvite.js";
import { useSafeBack } from "@nulife/chassis";
import { Home } from "./Home.js";
import * as api from "./api.js";

// ── The calendar is not on the first paint ──────────────────────────────────
//
// `CalendarScreen` pulls in FullCalendar and the time grid, which is most of
// this app's weight. Somebody opening the home screen to look at the tiles
// should not download it — same reasoning as Crafting Comb's, where the back
// office was 573kB in the chunk every visitor got.
const CalendarScreen = lazyWithReload(() =>
  import("./CalendarScreen.js").then(m => ({ default: m.CalendarScreen })));

/** Committee Decisions — its own chunk for the same reason. Somebody opening
 *  the calendar should not download the proposal screens either. */
const Decisions = lazyWithReload(() =>
  import("./Decisions.js").then(m => ({ default: m.Decisions })));

/** The document library, behind the Records tile. Its own chunk too. */
const Documents = lazyWithReload(() =>
  import("./Documents.js").then(m => ({ default: m.Documents })));

/** The compliance dashboard. */
/** The media library (issue #16), at /media. No home tile yet — see Media.tsx. */
const Media = lazyWithReload(() =>
  import("./Media.js").then(m => ({ default: m.Media })));
const Compliance = lazyWithReload(() =>
  import("./Compliance.js").then(m => ({ default: m.Compliance })));

/** Who is on the committee — `@nulife/people`, given a screen of its own for
 *  the first time (Drea, 2026-09-19). */
const Members = lazyWithReload(() =>
  import("./Members.js").then(m => ({ default: m.Members })));

/** Your own record, contact details only (2026-10-03). */
const MyProfile = lazyWithReload(() =>
  import("./MyProfile.js").then(m => ({ default: m.MyProfile })));

/** Messages between members — `@nulife/messaging`, switched on the same day. */
const Messages = lazyWithReload(() =>
  import("./Messages.js").then(m => ({ default: m.Messages })));

/** Discussion — `@nulife/discussion`, the first step of the Discussion →
 *  Decisions direction. Lazy like the rest: a committee that never opens it
 *  never downloads it. */
const Discussion = lazyWithReload(() =>
  import("./Discussion.js").then(m => ({ default: m.Discussion })));

/** Planned work — `@nulife/tasks`, the shared package extracted 2026-09-22. */
const Tasks = lazyWithReload(() =>
  import("./Tasks.js").then(m => ({ default: m.Tasks })));

/** Hall bookings — `@nulife/booking-ui`, Admin and Secretary only. Lazy for
 *  the same reason as the calendar: most visits never open it. */
const Bookings = lazyWithReload(() =>
  import("./Bookings.js").then(m => ({ default: m.Bookings })));
/** The Setup tile — today, the hall's hire types and opening hours. Same
 *  chunk as Bookings, and the same Admin/Secretary gate. */
const Setup = lazyWithReload(() =>
  import("./Setup.js").then(m => ({ default: m.Setup })));
/** The hall's clients (2026-10-04). Admin and Secretary, like Bookings. */
const Clients = lazyWithReload(() =>
  import("./Clients.js").then(m => ({ default: m.Clients })));

/** Minutes — the simple tile (2026-10-03): file, agree, email the committee. */
const Minutes = lazyWithReload(() =>
  import("./Minutes.js").then(m => ({ default: m.Minutes })));

/** Minutes Capture — `@nulife/minutes-capture`. V1 is the recording half.
 *  PAUSED 2026-10-03 (Drea): left in place and dormant at /minutes-capture,
 *  linked from nowhere, while she decides whether to build it at all. */
const MinutesCapture = lazyWithReload(() =>
  import("./MinutesCapture.js").then(m => ({ default: m.MinutesCapture })));

function usePath(): string {
  const [path, setPath] = useState(window.location.pathname);
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  return path;
}

export function App() {
  const path = usePath();
  const calendar = path.startsWith("/calendar");
  const bookingsView = path.startsWith("/bookings");
  const setupView = path === "/setup" || path === "/setup/";
  /** `/clients`, and `/clients/<person|organisation>/<id>` for one. */
  const clientsView = path === "/clients" || path.startsWith("/clients/");
  const openClient = (() => {
    const m = /^\/clients\/(person|organisation)\/([^/]+)\/?$/.exec(path);
    return m ? { kind: m[1] as "person" | "organisation", id: decodeURIComponent(m[2]!) } : null;
  })();
  /** Reached from the RECORDS tile — "minutes, policies and paperwork" is a
   *  document library, so it made that tile real rather than adding a ninth. */
  /**
   * `/welcome/<token>` — an access link.
   *
   * Farndale's own path, kept so a link generated by either product reads
   * the same. The token is in the PATH rather than a query string, which is
   * also Farndale's shape.
   */
  const welcomeToken = path.startsWith("/welcome/")
    ? decodeURIComponent(path.slice("/welcome/".length).replace(/\/$/, "")) || null
    : null;

  const documents = path.startsWith("/records");
  const mediaView = path === "/media" || path.startsWith("/media/");
  const compliance = path.startsWith("/compliance");
  const members = path.startsWith("/members");
  const profile = path === "/profile" || path === "/profile/";
  const tasksView = path.startsWith("/tasks");
  // `/minutes-capture` checked FIRST: it also starts with "/minutes".
  const minutesCaptureView = path.startsWith("/minutes-capture");
  const minutesView = !minutesCaptureView && path.startsWith("/minutes");

  /**
   * `/messages` for the inbox, `/messages/<threadId>` for one conversation.
   *
   * A real path rather than in-page state, for the same reason Committee
   * Decisions has one: a conversation is a thing somebody links to, comes back
   * to, and expects the back button to leave properly.
   */
  const messagesThread = path.startsWith("/messages/")
    ? decodeURIComponent(path.slice("/messages/".length).replace(/\/$/, "")) || null
    : null;
  const messages = path.startsWith("/messages");

  /** The Forum. `/discussion`, and `/discussion/<thread id>` to arrive with
   *  one thread open — the link a Task or Proposal's "Raised in discussion"
   *  line follows (2026-10-03). The thread still opens inside the Forum, and
   *  the server decides whether this person may see it at all. */
  const discussion = path === "/discussion" || path.startsWith("/discussion/");
  const discussionThread = path.startsWith("/discussion/")
    ? decodeURIComponent(path.slice("/discussion/".length).replace(/\/$/, "")) || null
    : null;

  // ── Committee Decisions ─────────────────────────────────────────────────
  //
  // `/decisions`, `/decisions/history`, and `/decisions/<id>` for one matter.
  // Real paths rather than in-page state, so a proposal can be linked to in an
  // email — which is exactly what a committee will do with it.
  const decisionsView = path === "/decisions" || path === "/decisions/"
    ? { name: "list" as const }
    : path === "/decisions/history" || path === "/decisions/history/"
      ? { name: "history" as const }
      : path.startsWith("/decisions/")
        ? { name: "detail" as const, id: decodeURIComponent(path.slice("/decisions/".length).replace(/\/$/, "")) }
        : null;

  const go = (to: string) => {
    window.history.pushState({}, "", to);
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  /**
   * Back one step, and Home as a separate destination — Farndale's rule.
   *
   * ── The bug this fixes (2026-09-24) ─────────────────────────────────────
   *
   * Every screen was given `onBack={() => go("/")}`, and every screen passed
   * that straight into the frame's `onHome`. So the app had exactly one
   * back-up control and it went to the tile grid from everywhere: leaving a
   * proposal you had opened from the Messages screen threw you to Home, and
   * the way back to where you actually were was to start again.
   *
   * Farndale's own frame is explicit that these are two different things:
   * "Home is a destination, not a back button — it answers 'where do I live'
   * and never 'undo that tap'." Its `useSafeBack` is what implements that, and
   * it is exported from the chassis; Memorial Hall simply never used it.
   *
   * `useSafeBack` and not a bare `history.back()`: on a page opened directly —
   * a link in an email to one matter, a home-screen shortcut — stepping back
   * leaves the app entirely and lands on a blank tab. It records the history
   * length at mount, so it knows whether THIS app put anything there, and
   * falls back to the tile grid when it did not.
   */
  const goBack = useSafeBack("/", go);
  const goHome = () => go("/");

  const config = useAsync(() => api.getSettings(), []);

  // Who is signed in. Asked on every load, and re-asked whenever the server
  // says 401 — a session that ends mid-use returns the app to the sign-in form
  // instead of leaving a screen of broken panels.
  const me = useAsync(() => api.getMe(), []);

  /**
   * Whether Ask Mira is connected, asked ONCE for the whole app.
   *
   * Every screen inside `AppFrame` carries the assistant bar, and all three
   * must give the same answer — a bar that is a live button on Home and a
   * "not connected" strip on Members would be two claims about one thing.
   */
  // Asked only once there is a session to ask with, and asked AGAIN when that
  // session changes. Fetching it on load hit the sign-in gate, failed with a
  // 401, and left every screen saying "could not be reached" for the rest of
  // the visit — a broken-looking bar that was only ever a request made too
  // early.
  const signedIn = me.data?.signedIn === true;
  const assistant = useAsync(
    () => signedIn ? api.getAssistantStatus() : Promise.resolve(null),
    [signedIn],
  );
  const assistantUnavailable =
    !signedIn || (assistant.loading && !assistant.data) ? "Checking whether Ask Mira is connected…"
    : assistant.error ? "Ask Mira could not be reached just now"
    : assistant.data?.available ? undefined
    : assistant.data?.reason ?? "Ask Mira is not connected to this app yet";
  useEffect(() => { api.whenSignedOut(() => me.reload()); }, [me.reload]);

  // Nothing renders until the organisation's timezone is known. Rendering
  // first and correcting afterwards would show every time on the viewer's
  // clock for a moment — and somebody who reads the page in that moment is
  // reading wrong times.
  if (!config.data) {
    return (
      <Shell>
        {config.error
          ? <Message tone="bad">Could not load the hall's details. Please refresh.</Message>
          : <Loading />}
      </Shell>
    );
  }

  /**
   * An access link, BEFORE the sign-in gate.
   *
   * It has to render for somebody with no session — that is the entire
   * point of it — so it is handled here rather than inside the signed-in
   * branch below. Checked after `config` only so the page can carry the
   * hall's own theme.
   *
   * It also takes precedence over an existing session: somebody who is
   * already signed in as themselves and opens a link meant for Andy must
   * not silently have it swallowed by the app rendering Home instead. They
   * see whose link it is and can decide.
   */
  if (welcomeToken !== null) {
    return (
      <Shell theme={config.data.theme}>
        <AcceptInvite token={welcomeToken} onIn={() => { me.reload(); go("/"); }} />
      </Shell>
    );
  }

  /**
   * The home screen is full-bleed; every other screen keeps the reading
   * column.
   *
   * `AppFrame` is its own `min-h-screen` shell with a hero, a pulse strip, a
   * module strip and a sticky assistant bar — furniture that runs edge to
   * edge. Nested inside Shell's centred 880px column it rendered as a phone
   * screen floating in the middle of a desktop, which is not the design.
   */
  /** Screens that bring their own full-width `AppFrame`. The rest keep
   *  Shell's centred reading column. */
  /**
   * EVERY signed-in screen is framed now (2026-09-27).
   *
   * This list existed because Calendar, Compliance and Documents drew their
   * own header and would have rendered two sets of chrome. They are all on
   * `AppFrame` now, so the exception list is empty — and it is kept as a
   * constant rather than deleted so the next screen has somewhere obvious
   * NOT to be added.
   */
  const framed = me.data?.signedIn === true;

  return (
    <Shell theme={config.data.theme} bleed={framed}>
      {me.data?.signedIn
        ? (
          <Suspense fallback={<Loading />}>
            {calendar
              ? <CalendarScreen
                  config={config.data}
                  onBack={goBack}
                  onHome={goHome}
                  assistantUnavailable={assistantUnavailable}
                  // A booking on the calendar is Booking's: tapping one goes
                  // there — for whoever may manage bookings. For anybody else
                  // it is read-only and goes nowhere, honestly.
                  onOpenBooking={me.data.canManageBookings ? () => go("/bookings") : undefined}
                />
              : bookingsView && me.data.canManageBookings
                ? <Bookings config={config.data} onBack={goBack} onHome={goHome}
                    onClients={() => go("/clients")}
                    assistantUnavailable={assistantUnavailable} />
              : clientsView && me.data.canManageBookings
                ? <Clients open={openClient} onBack={goBack} onHome={goHome}
                    onOpen={c => go(c ? `/clients/${c.kind}/${encodeURIComponent(c.id)}` : "/clients")}
                    assistantUnavailable={assistantUnavailable} />
              : setupView && me.data.canManageBookings
                ? <Setup onBack={goBack} onHome={goHome} canConfigure={me.data.canConfigure}
                    assistantUnavailable={assistantUnavailable} />
              : compliance
                ? <Compliance onBack={goBack} onHome={goHome}
                    assistantUnavailable={assistantUnavailable} />
                : profile
                ? <MyProfile onBack={goBack} onHome={goHome}
                    assistantUnavailable={assistantUnavailable} />
                : members
                ? <Members
                    onProfile={() => go("/profile")}
                    assistantUnavailable={assistantUnavailable}
                    canConfigure={me.data.canConfigure}
                    onBack={goBack} onHome={goHome} onMessage={async personId => {
                    // Straight into the conversation, creating it if this is
                    // the first thing either of them has said.
                    const { threadId } = await api.openConversationWith(personId);
                    go(`/messages/${threadId}`);
                  }} />
                : tasksView
                ? <Tasks onBack={goBack} onHome={goHome} assistantUnavailable={assistantUnavailable} />
                : minutesCaptureView
                ? <MinutesCapture onBack={goBack} onHome={goHome} assistantUnavailable={assistantUnavailable} />
                : minutesView
                ? <Minutes onBack={goBack} onHome={goHome} assistantUnavailable={assistantUnavailable} />
                : messages
                ? <Messages
                    assistantUnavailable={assistantUnavailable}
                    threadId={messagesThread}
                    onBack={goBack}
                    onHome={goHome}
                    onOpenThread={id => go(id ? `/messages/${id}` : "/messages")}
                    onMembers={() => go("/members")}
                    canConfigure={me.data.canConfigure}
                  />
                : discussion
                ? <Discussion me={me.data} onBack={goBack} onHome={goHome}
                    threadId={discussionThread}
                    assistantUnavailable={assistantUnavailable} />
                : mediaView
                ? <Media onBack={goBack} onHome={goHome}
                    assistantUnavailable={assistantUnavailable} />
                : documents
                ? <Documents me={me.data} onBack={goBack} onHome={goHome}
                    assistantUnavailable={assistantUnavailable} />
                : decisionsView
                  ? <Decisions
                      me={me.data} view={decisionsView} onGo={go}
                      onBack={goBack} onHome={goHome}
                      assistantUnavailable={assistantUnavailable}
                    />
                  : <Home
                      me={me.data}
                      config={config.data}
                      assistantUnavailable={assistantUnavailable}
                      onGo={go}
                      onSignedOut={() => me.reload()}
                    />}
          </Suspense>
        )
        : <SignInForm
            organisationName={config.data.organisationName}
            heading="Committee calendar"
            submit={api.signIn}
            onSignedIn={() => me.reload()}
            resetHint={<>
              Forgotten it? It can be reset on the machine that runs this:
              {" "}<code>npm run set-password</code>.
            </>}
          />}
    </Shell>
  );
}

/**
 * The app's visual root.
 *
 * ── `data-nl-theme` is not decoration; without it there is no styling ───────
 *
 * Every token in `@nulife/booking-ui`'s theme.css is declared under
 * `[data-nl-theme] { … }`. An app root that does not set it resolves not one
 * of those custom properties — every `var(--nl-ink)`, `var(--nl-surface)` and
 * `var(--nl-state-open)` falls back to nothing, and the whole product renders
 * as unstyled black text on white. That happened to Crafting Comb on
 * 2026-09-11 and took a screenshot to notice, because the colour fallbacks in
 * the stylesheets made parts of it still come out teal. Those fallbacks are
 * gone now, so the same mistake here would be obvious in the first second.
 *
 * `"nulife"` when unset rather than omitting the attribute: the tokens are
 * declared under `[data-nl-theme]`, so the attribute has to be PRESENT even
 * when there is nothing to override. This tenant has no theme of its own —
 * nobody has said what the hall looks like — so it is on the platform palette,
 * which is what `"nulife"` means.
 */
function Shell({ theme, bleed, children }: {
  theme?: string | null;
  /** Skip the centred reading column — for a screen that brings its own
   *  full-width frame. See `home` above. */
  bleed?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      data-nl-theme={theme || "nulife"}
      /* `?flat=1` previews the Add/View buttons without their gloss, so Drea
         can compare the two on the same screen before deciding. Off unless
         asked for — see `styles.css`. */
      {...(typeof window !== "undefined"
        && new URLSearchParams(window.location.search).has("flat")
        ? { "data-nl-flat": "" } : {})}
      style={{
        minHeight: "100vh",
        background: "var(--nl-bg)",
        color: "var(--nl-ink)",
        // `fontFamily`, not `font`. The `font` shorthand REQUIRES a size and a
        // family together; given only a family the whole declaration is
        // invalid and dropped, so the app silently keeps the browser default.
        fontFamily: "var(--nl-font)",
        fontSize: "1rem",
        lineHeight: 1.45,
      }}
    >
      {bleed ? children : (
        <div style={{ maxWidth: 880, margin: "0 auto", padding: "20px 16px 64px" }}>
          {children}
        </div>
      )}
    </div>
  );
}
